import jwt_decode from "jwt-decode";
import { createStore, applyMiddleware } from "redux";
import createSagaMiddleware from "redux-saga";
import rootReducer from "./reducers";
import rootSaga from "./sagas";
import {
    AD_CLICK_URL_ONLY_KEYS,
    ATTRIBUTION_STORAGE_KEY,
    buildPathFromSafeQuery,
    persistAttribution,
} from "./utils/attribution";
import { clearLocalStoragePreservingKeys } from "./utils/localStorage";
import { CHOICE_STORAGE_KEY } from "./utils/replay-consent";
import { PAID_SIGNUP_TRACKED_STORAGE_KEY } from "./utils/tracking";
import { captureReferralCode } from "./utils/referral";
import {
    isDailyAbsiteEnabled,
    isOpaqueDailyEntry,
    sanitizeOpaqueDailyEntry,
} from "./utils/daily-question-opaque";
import url from "./constants/urlList";
import { qlinkIdFromPath, rememberQLinkShareToken } from "./utils/qlink-share-token";
import {
    buildLoginRedirectPath,
    createStaleSessionGuard,
    SESSION_CLEARED_EVENT,
    STALE_SESSION_KEYS,
} from "./utils/stale-session";
import { createQuizLoginPath, practiceEntryLoginPath } from "./utils/absite-practice-intent";
import { PRACTICE_EMAIL_PENDING_KEY } from "./utils/growth-receipt";
import { isTokenDeadBeyondSkew } from "./utils/session";

// This runs during module evaluation, before referral capture and before React
// mounts. On the Daily ABSITE landing and its opaque login-return path the
// campaign tags are saved first-party first, through persistAttribution's own
// allowlist as on every other route (ads attribution contract, 2026-10-20; the
// September design stripped them unsaved). The clean-up then keeps only the
// opaque state and the campaign keys in the address, so referral capture below
// never sees `ref` there.
if (isDailyAbsiteEnabled() && isOpaqueDailyEntry()) {
    try {
        persistAttribution();
    } catch {
        // Storage blocked: the address is still cleaned below.
    }
}
export const opaqueDailyEntry = isDailyAbsiteEnabled()
    ? sanitizeOpaqueDailyEntry()
    : {};
const sagaMiddleware = createSagaMiddleware();

// The trusted QLearn API origin for the unauthenticated Apple exchange: derived from the exact
// URL the API layer emits (${domain}/qlearn/auth/apple), so the exemption follows the configured
// prod/dev/preprod host and nothing else.
const APPLE_EXCHANGE_PATHNAME = "/qlearn/auth/apple";
const trustedApiOrigin = (() => {
    try { return new URL(url.appleAuth, window.location.origin).origin; } catch (e) { return null; }
})();
const trustedDailyQuestionOrigin = (() => {
    try { return new URL(url.dailyQuestion, window.location.origin).origin; } catch (e) { return null; }
})();
const DAILY_QUESTION_PATHNAME = (() => {
    try {
        return new URL(url.dailyQuestion, window.location.origin).pathname.replace(/\/$/, "");
    } catch (e) {
        return "/qlearn/daily-question";
    }
})();

export const isPublicDailyQuestionRequest = (requestUrl, requestOptions) => {
    if (!isDailyAbsiteEnabled()) return false;
    try {
        const u = new URL(requestUrl, window.location.origin);
        const trustedOrigin = u.origin === window.location.origin
            || (trustedDailyQuestionOrigin !== null && u.origin === trustedDailyQuestionOrigin);
        if (!trustedOrigin || !u.pathname.startsWith(`${DAILY_QUESTION_PATHNAME}/`)) {
            return false;
        }
        const suffix = u.pathname.slice(DAILY_QUESTION_PATHNAME.length + 1);
        const method = (requestOptions?.method || "GET").toUpperCase();
        return (method === "GET" && (
            /^[^/]+\/?$/.test(suffix)
            || /^[^/]+\/[^/]+\/[^/]+\/[^/]+\/?$/.test(suffix)
            || /^touch\/dqt_[0-9a-f]{64}\/?$/.test(suffix)
        ))
            || (method === "POST" && (
                /^[^/]+\/answer\/?$/.test(suffix)
                || /^[^/]+\/unlock-grant\/?$/.test(suffix)
            ));
    } catch {
        return false;
    }
};

const store = createStore(rootReducer, applyMiddleware(sagaMiddleware));

// then run the saga
sagaMiddleware.run(rootSaga);

export default store;
let token, isExpired = false;

export const buildQlinkEntryRedirectUrl = ({ origin, pathname, search }) => (
    `${origin}${buildPathFromSafeQuery(pathname, search)}`
);

// Referral capture now lives in utils/referral (it stores a companion
// capture timestamp so reads can expire a stale code). Kept as a thin
// re-export here so the module-load + fetch-interceptor call sites and
// store.test.js are unchanged.
export const captureReferralCodeFromSearch = (
    search = window.location.search,
    storage = window.localStorage
) => captureReferralCode(search, storage);

captureReferralCodeFromSearch();

// /qlearn/auth/apple is an unauthenticated session exchange (native Sign in with
// Apple), like signin/signup/phone — it must not receive a "Bearer null" or stale
// token. Match it by EXACT normalized pathname (not a whole-URL substring): a
// substring match would also strip auth from unrelated protected URLs such as
// /qlearn/user/profile?return=/qlearn/auth/apple and lookalikes like
// /qlearn/auth/apple-admin.
const isAppleAuthExchange = (requestUrl) => {
    try {
        const u = new URL(requestUrl, window.location.origin);
        if (u.pathname !== APPLE_EXCHANGE_PATHNAME) return false;
        // Exact pathname AND a trusted origin: same-origin (the intended relative form)
        // or the configured QLearn API origin. An alternate-origin exact-path URL
        // (e.g. https://alternate.test/qlearn/auth/apple) retains normal Authorization.
        return u.origin === window.location.origin
            || (trustedApiOrigin !== null && u.origin === trustedApiOrigin);
    } catch {
        return false; // unparseable URL: not the exchange, keep normal auth
    }
};

// Fail safe on a malformed stored token: treat it as expired (the auth-page
// clear-storage self-heal then recovers the session) instead of throwing out
// of every fetch (GPT-5.6 finding 6 — defense in depth behind the persist-time
// guard). Number() + isFinite: a truthy NON-numeric exp must read as expired —
// 'Date.now() >= NaN' is false, which sent claims-broken tokens on every
// protected request (round-2 finding 5).
export const decodeStoredToken = (storedToken) => {
    try {
        const decoded = jwt_decode(storedToken);
        const expMs = Number(decoded?.exp) * 1000;
        return { decoded, expired: !Number.isFinite(expMs) || Date.now() >= expMs };
    } catch {
        return { decoded: undefined, expired: true }; // malformed: treat as expired
    }
};

const isQlinkEntryPath = (pathname) =>
    pathname.includes("/question/view") || pathname.includes("/qlink/preview");

// Ad click ids that are not part of our stored attribution but that GTM's tags
// read from the page URL (the conversion linker; Reddit's pixel reads rdt_cid).
// The tag containers now start after this strip on QLink routes (index.html),
// so the strip must keep them in the URL (never in storage) or Ads loses
// iOS/app clicks (gbraid/wbraid). The ad click keys every cleaned sign-in entry
// keeps, plus GA's cross-domain _gl.
const QLINK_ENTRY_URL_ONLY_KEYS = [...AD_CLICK_URL_ONLY_KEYS, "_gl"];

const buildQlinkEntryStrippedPath = ({ origin, pathname, search, hash }) => {
    const stripped = new URL(buildPathFromSafeQuery(pathname, search, {
        hash,
        urlOnlyParamKeys: QLINK_ENTRY_URL_ONLY_KEYS,
    }), origin);
    return `${stripped.pathname}${stripped.search}${stripped.hash}`;
};

// Landing speed: a QLink arrives with ?token=<share token>. Store it and strip
// it from the address bar NOW, while this module evaluates (before React and
// the router read the URL and before the first API call), with replaceState.
// This used to happen on the first fetch via a location.href assignment — a
// full second document load on every shared-question landing. Same end state:
// the token lives in storage only, and the URL keeps the allowlisted
// attribution keys buildPathFromSafeQuery keeps plus the URL-only click ids
// above. Fails open like the head
// url-secret-scrub: if storage throws, the URL is left alone and the
// interceptor fallback below behaves as before.
export const captureQlinkEntryTokenInPlace = (win = window) => {
    try {
        const { origin, pathname, search, hash } = win.location;
        if (!isQlinkEntryPath(pathname)) return false;
        const shareToken = new URLSearchParams(search).get("token");
        if (!shareToken) return false;
        win.localStorage.setItem("userAcquisitiontoken", shareToken);
        rememberQLinkShareToken(qlinkIdFromPath(pathname), shareToken);
        win.history.replaceState(win.history.state, "", buildQlinkEntryStrippedPath({ origin, pathname, search, hash }));
        return true;
    } catch {
        // Fail open (storage blocked, odd URL): leave the URL as it is; the
        // interceptor fallback handles the token as before.
        return false;
    }
};

captureQlinkEntryTokenInPlace();

// A returning visitor whose stored login is dead (the 24 h token expired days
// ago, or a role/userdetails left without a usable token) is a signed-OUT
// visitor on a QLink. Left in place, the page took the signed-in path: the
// public answer check was skipped, the explanation request 401'd with the dead
// token, and the drawer dead-ended on "We couldn't load the explanation" with
// no sign-in (owner iPhone smoke 2026-10-01). Runs once per document, here,
// before React mounts and before any request, so no request carries the dead
// token and a sign-in later in this document is never undone. Session identity
// only (the stale-session guard's set): the share token, attribution, referral,
// OAuth-return and pending-email-verify keys stay. Expiry allows for clock skew.
export const clearDeadQLinkSessionInPlace = (win = window, now = Date.now()) => {
    try {
        if (!isQlinkEntryPath(win.location.pathname)) return false;
        const storage = win.localStorage;
        if (!STALE_SESSION_KEYS.some((key) => storage.getItem(key) !== null)) return false;
        if (!isTokenDeadBeyondSkew(storage.getItem("token"), now)) return false;
        STALE_SESSION_KEYS.forEach((key) => storage.removeItem(key));
        return true;
    } catch {
        // Storage blocked: leave everything as it was (fail open).
        return false;
    }
};

clearDeadQLinkSessionInPlace();

// Fallback for a token that reaches the URL after this module loaded (e.g. an
// in-app or native deep-link route change to /question/view?token=…; a normal
// landing never gets here because the capture above already emptied the URL):
// store it and reload on the scrubbed URL, as before.
const captureQlinkEntryToken = () => {
    const userAcqToken = new URLSearchParams(window.location.search).get('token');
    if (!userAcqToken) return;
    window.localStorage.setItem("userAcquisitiontoken", userAcqToken);
    // Per-link, this-tab copy (sessionStorage, never a URL) so a
    // reload/Back still works after the shared key is cleared.
    rememberQLinkShareToken(qlinkIdFromPath(window.location.pathname), userAcqToken);
    window.location.href = buildQlinkEntryRedirectUrl({
        origin: window.location.origin,
        pathname: window.location.pathname,
        search: window.location.search
    });
};

// Pages the expiry redirect below must never leave (sign-in, recovery and
// public-info surfaces, and the public Daily landing).
const AUTH_PAGE_PATH_FRAGMENTS = [
    "/login",
    "/register",
    "/forget-password",
    "/reset-password",
    "/verify-user",
    "/socialAuth",
    "/privacy-policy",
    "/terms",
    "/support",
    "/absite-program-code-request",
    "/daily-absite-question",
    // After account deletion: the session is gone on purpose.
    "/account-deleted",
    // The quiz-link claim page holds a signed-out student in place to show
    // which address to sign in with; its own open ping is tokenless.
    "/notes-to-quiz/",
];
// The only hosts that may receive the user's session token: the QLearn API origin
// the API layer is configured for (prod, dev, preprod or an override) and this page's
// own origin. Anything that does not parse as a URL is treated as foreign.
export const isQlearnApiRequest = (requestUrl) => {
    try {
        const origin = new URL(requestUrl, window.location.origin).origin;
        return origin === window.location.origin
            || (trustedApiOrigin !== null && origin === trustedApiOrigin);
    } catch (e) {
        return false;
    }
};
// Adds the bearer token to whatever headers shape a caller passed: none (e.g.
// { method: "POST" }), a plain object, a Headers instance or an array of
// [name, value] pairs. Bracket assignment alone threw on a missing headers object
// and only set an inert property on a Headers instance.
const withBearerToken = (headers, token) => {
    const value = "Bearer " + token;
    if (!headers) {
        return { Authorization: value };
    }
    if (typeof Headers !== "undefined" && headers instanceof Headers) {
        headers.set("Authorization", value);
        return headers;
    }
    if (Array.isArray(headers)) {
        return headers
            .filter((pair) => String(pair && pair[0]).toLowerCase() !== "authorization")
            .concat([["Authorization", value]]);
    }
    Object.keys(headers)
        .filter((name) => name !== "Authorization" && name.toLowerCase() === "authorization")
        .forEach((name) => { delete headers[name]; });
    headers["Authorization"] = value;
    return headers;
};

const isAuthPagePath = (pathname) =>
    AUTH_PAGE_PATH_FRAGMENTS.some((fragment) => pathname.includes(fragment));

// The /login address for a visitor leaving Create Quiz: the practice entry
// (/create-quiz?practice=absite) returns to itself, any other Create Quiz
// address to /create-quiz, and both keep the campaign keys and ad click ids
// for GTM's tags on /login. undefined for every other page.
const createQuizSignInPath = (pathname, search) =>
    practiceEntryLoginPath(pathname, search) || createQuizLoginPath(pathname, search);

// Where the interceptor below sends a visitor with no usable token.
export const signedOutRedirectTarget = (location) =>
    createQuizSignInPath(location?.pathname, location?.search) || "/";

// Interceptor
(function () {
    const originalFetch = window.fetch;
    // A token the server rejects while its client-side exp is still valid
    // (secret rotation, tokenVersion revocation) is cleared and the user sent
    // to /login — gated and confirmed against GET /qlearn/user/me, see
    // utils/stale-session. The probe uses the ORIGINAL fetch (no recursion).
    const staleSessionGuard = createStaleSessionGuard({
        probeUrl: url.userDetailsFromToken,
        fetchImpl: (...args) => originalFetch.apply(window, args),
        storage: window.localStorage,
        getPathname: () => window.location.pathname,
        // Create Quiz signs in like a signed-out visit (the practice link
        // returns to itself, campaign keys kept); every other page keeps only
        // its pathname.
        loginRedirectPath: (path) => createQuizSignInPath(path, window.location.search)
            || buildLoginRedirectPath(path),
        redirectToLogin: (target) => window.location.replace(target),
        baseUrl: window.location.origin,
        // A public entry route (QLink) is cleared without navigating; its
        // page listens for this and drops to its signed-out path.
        onCleared: () => window.dispatchEvent(new Event(SESSION_CLEARED_EVENT)),
    });
    window.fetch = function (url, options) {
        // The session token is for QLearn's own API. A request to any other host
        // (analytics, ad and recording vendors call window.fetch too) goes out exactly
        // as its caller made it: no bearer token, no session clearing, no redirect.
        const requestUrl = typeof url === "string" ? url : String((url && url.url) || url);
        if (!isQlearnApiRequest(requestUrl)) {
            return originalFetch.apply(this, arguments);
        }
        let mainArguments = arguments;
        let sentToken = null;
        // Skip auth header for login/signup endpoints, but allow it for profile/complete.
        const skipAuthHeader = (requestUrl.match("/auth/signin") || requestUrl.match("/auth/signup") ||
                               isAppleAuthExchange(requestUrl) ||
                               isPublicDailyQuestionRequest(requestUrl, options) ||
                               requestUrl.match("/auth/phone/send-otp") || requestUrl.match("/auth/phone/verify-otp") ||
                               // Anonymous institutional sign-in (the signed-in /account/institutional/*
                               // link routes do NOT match and keep the bearer token).
                               requestUrl.match("/auth/institutional/send-otp") || requestUrl.match("/auth/institutional/verify-otp") ||
                               requestUrl.match("/diagnostic/absite/program-codes/request") ||
                               requestUrl.match("/js/"));
        if (!skipAuthHeader) {
            if (!options) {
                options = {
                    headers: {},
                };
                // Replace, not append: fetch(url, undefined) must not leave the new
                // options in a third argument that fetch ignores.
                mainArguments = [arguments[0], options];
            }

            token = window.localStorage.getItem("token");

            if (token) {
                isExpired = decodeStoredToken(token).expired;
            }

            captureReferralCodeFromSearch();

            const isQlinkEntry = isQlinkEntryPath(window.location.pathname);

            if (isQlinkEntry) {
                captureQlinkEntryToken();
            }

            const isAuthPage = isAuthPagePath(window.location.pathname);

            // The stale-session guard just cleared a server-rejected token and
            // issued /login; a request fired before that navigation commits
            // (a poll, a saga retry) must not start the full clear + "/"
            // redirect below, which would wipe keys the guard preserves. If
            // that navigation was cancelled (exam "Leave site?" → Stay), offer
            // it again, throttled.
            if (staleSessionGuard.passThroughAfterClear(token)) {
                return originalFetch.apply(this, mainArguments);
            }

            if (!isAuthPage && !isQlinkEntry && (!token || isExpired)) {
                // The session-recording choice is the browser's, not the
                // session's: a refusal must survive this clear (privacy policy
                // 2.4, "we then remember your choice in your browser").
                // A practice-email landing captured on this page waits for
                // the sign-in this clear leads to (utils/growth-receipt).
                clearLocalStoragePreservingKeys(window.localStorage, [
                    ATTRIBUTION_STORAGE_KEY,
                    PAID_SIGNUP_TRACKED_STORAGE_KEY,
                    CHOICE_STORAGE_KEY,
                    PRACTICE_EMAIL_PENDING_KEY,
                ]);
                // "/" sends a signed-out visitor to /login, which then goes to
                // Create Quiz. On Create Quiz itself the page goes straight to
                // /login with a ?next= back (the day-2 email's practice link
                // to itself, so the practice survives the sign-in), keeping
                // the link's campaign keys and ad click ids in the address.
                window.location = signedOutRedirectTarget(window.location);
                return;
            }

            const headersWithToken = withBearerToken(options.headers, token);
            if (headersWithToken !== options.headers) {
                options.headers = headersWithToken;
            }
            sentToken = token;
        }

        // Side observer only: the caller still gets the original promise and
        // response object; the observer swallows its own errors.
        return staleSessionGuard.watch(originalFetch.apply(this, mainArguments), { requestUrl, sentToken });
    };
})();
